function initPasswordToggles(root = document) {
  const toggles = Array.from(root.querySelectorAll('[data-password-toggle]'));
  if (!toggles.length) return;

  toggles.forEach((toggle) => {
    if (toggle.dataset.passwordToggleInitialized === 'true') return;
    toggle.dataset.passwordToggleInitialized = 'true';

    const inputSelector = toggle.getAttribute('data-password-input');
    const input = inputSelector ? document.querySelector(inputSelector) : null;
    if (!input) return;

    const syncState = () => {
      const isVisible = input.getAttribute('type') === 'text';
      toggle.classList.toggle('is-visible', isVisible);
      toggle.setAttribute('aria-pressed', String(isVisible));
    };

    const activate = () => {
      input.setAttribute('type', input.getAttribute('type') === 'password' ? 'text' : 'password');
      syncState();
      input.focus({ preventScroll: true });
    };

    syncState();
    toggle.addEventListener('click', activate);

    toggle.addEventListener('keydown', (event) => {
      const key = event.key;
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        activate();
      }
    });
  });
}

function initConfirmPasswordValidation(root = document) {
  const forms = Array.from(root.querySelectorAll('form[data-confirm-password-form]'));
  if (!forms.length) return;

  forms.forEach((form) => {
    if (form.dataset.confirmPasswordInitialized === 'true') return;
    if (form.hasAttribute('data-register-form')) return;
    form.dataset.confirmPasswordInitialized = 'true';

    const password = form.querySelector('[data-password]');
    const confirm = form.querySelector('[data-confirm-password]');
    if (!password || !confirm) return;

    const passwordErrorEl = form.querySelector('[data-password-strength-error]');
    const matchErrorEl = form.querySelector('[data-password-match-error]');

    const isPasswordStrong = (val) => {
      return val.length >= 8
        && /[A-Z]/.test(val)
        && /[a-z]/.test(val)
        && /[0-9]/.test(val)
        && /[^A-Za-z0-9]/.test(val);
    };

    if (passwordErrorEl) { passwordErrorEl.hidden = true; passwordErrorEl.style.display = 'none'; }
    if (matchErrorEl) { matchErrorEl.hidden = true; matchErrorEl.style.display = 'none'; }

    const validate = () => {
      const ok = !password.value || !confirm.value || password.value === confirm.value;
      confirm.toggleAttribute('aria-invalid', !ok);
      return ok;
    };

    form.addEventListener('submit', (event) => {
      if (!isPasswordStrong(password.value || '')) {
        event.preventDefault();
        if (passwordErrorEl) { passwordErrorEl.hidden = false; passwordErrorEl.style.display = ''; }
        password.focus({ preventScroll: true });
        return;
      }
      if (passwordErrorEl) { passwordErrorEl.hidden = true; passwordErrorEl.style.display = 'none'; }

      if (!validate()) {
        event.preventDefault();
        if (matchErrorEl) { matchErrorEl.hidden = false; matchErrorEl.style.display = ''; }
        confirm.focus({ preventScroll: true });
        return;
      }
      if (matchErrorEl) { matchErrorEl.hidden = true; matchErrorEl.style.display = 'none'; }
    });

    confirm.addEventListener('input', validate);
    password.addEventListener('input', () => {
      validate();
      if (passwordErrorEl && !passwordErrorEl.hidden && isPasswordStrong(password.value || '')) {
        passwordErrorEl.hidden = true;
        passwordErrorEl.style.display = 'none';
      }
    });
  });
}

function initRegisterNoteComposer(root = document) {
  const forms = Array.from(root.querySelectorAll('form[data-register-form]'));
  if (!forms.length) return;

  forms.forEach((form) => {
    if (form.dataset.registerNoteInitialized === 'true') return;
    form.dataset.registerNoteInitialized = 'true';

    const noteInput = form.querySelector('[data-customer-note]');
    if (!noteInput) return;

    const gender = form.querySelector('[data-gender]');
    const dob = form.querySelector('[data-dob]');
    const privacy = form.querySelector('[data-privacy]');

    const buildNote = () => {
      const parts = [];
      gender?.value && parts.push(`Gender: ${gender.value}`);
      dob?.value && parts.push(`Date of Birth: ${dob.value}`);
      privacy && parts.push(`Privacy accepted: ${privacy.checked ? 'Yes' : 'No'}`);
      return parts.join('\n');
    };

    const syncNote = () => {
      noteInput.value = buildNote();
    };

    [gender, dob, privacy].forEach((el) => {
      if (!el) return;
      el.addEventListener('change', syncNote);
      el.addEventListener('input', syncNote);
    });

    syncNote();
  });
}

function initRegisterNameMapper(root = document) {
  const forms = Array.from(root.querySelectorAll('form[data-register-form]'));
  if (!forms.length) return;

  forms.forEach((form) => {
    if (form.dataset.registerNameInitialized === 'true') return;
    form.dataset.registerNameInitialized = 'true';

    const fullName = form.querySelector('[data-full-name]');
    const first = form.querySelector('#RegisterForm-FirstName');
    const last = form.querySelector('#RegisterForm-LastName');
    if (!fullName || !first || !last) return;

    const map = () => {
      const raw = (fullName.value || '').trim();
      if (!raw) return;
      const tokens = raw.split(/\s+/).filter(Boolean);
      first.value = tokens.shift() || '';
      last.value = tokens.join(' ');
    };

    fullName.addEventListener('input', map);
    form.addEventListener('submit', map);
  });
}

function initRegisterFormValidation(root = document) {
  const forms = Array.from(root.querySelectorAll('form[data-register-form]'));
  if (!forms.length) return;

  const sanitizeNameValue = (value) => {
    return (value || '').replace(/[^A-Za-z\s]/g, '');
  };

  forms.forEach((form) => {
    if (form.dataset.registerValidationInitialized === 'true') return;
    form.dataset.registerValidationInitialized = 'true';

    const submitBtn = form.querySelector('button[type="submit"], button:not([type]), input[type="submit"]');
    if (!submitBtn) return;

    const password = form.querySelector('[data-password]');
    const confirm = form.querySelector('[data-confirm-password]');
    const dob = form.querySelector('[data-dob]');
    const privacy = form.querySelector('[data-privacy]');

    const syncDobFilledState = () => {
      if (!dob) return;
      dob.classList.toggle('filled', Boolean((dob.value || '').trim()));
    };

    const firstName = form.querySelector('input[name="customer[first_name]"]');
    const lastName = form.querySelector('input[name="customer[last_name]"]');

    const sanitizeNames = () => {
      if (firstName) firstName.value = sanitizeNameValue(firstName.value);
      if (lastName) lastName.value = sanitizeNameValue(lastName.value);
    };

    const requiredEls = Array.from(form.querySelectorAll('input[required], select[required], textarea[required], [aria-required="true"]'));

    const setSubmitEnabled = (enabled) => {
      submitBtn.disabled = !enabled;
      submitBtn.setAttribute('aria-disabled', String(!enabled));
    };

    const isRequiredFilled = (el) => {
      if (!el) return true;
      if (el.disabled) return true;

      const isRequired = el.hasAttribute('required') || el.getAttribute('aria-required') === 'true';
      if (!isRequired) return true;

      const tag = (el.tagName || '').toLowerCase();
      const type = (el.getAttribute('type') || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') return Boolean(el.checked);
      if (tag === 'select') return Boolean((el.value || '').trim());
      return (el.value || '').trim() !== '';
    };

    const passwordErrorEl = form.querySelector('[data-password-strength-error]');

    const isPasswordStrong = (val) => {
      return val.length >= 8
        && /[A-Z]/.test(val)
        && /[a-z]/.test(val)
        && /[0-9]/.test(val)
        && /[^A-Za-z0-9]/.test(val);
    };

    const showPasswordError = (show) => {
      if (!passwordErrorEl) return;
      passwordErrorEl.hidden = !show;
      passwordErrorEl.style.display = show ? '' : 'none';
    };

    const enforcePasswordStrength = () => {
      if (!password) return true;
      const strong = isPasswordStrong(password.value || '');
      showPasswordError(!strong);
      return strong;
    };

    const blockWeakPassword = (event) => {
      if (enforcePasswordStrength()) return false;
      event?.preventDefault?.();
      event?.stopImmediatePropagation?.();
      password?.focus({ preventScroll: true });
      return true;
    };

    const validatePasswordMatch = () => {
      if (!password || !confirm) return true;
      const p = (password.value || '').trim();
      const c = (confirm.value || '').trim();

      if (!p || !c) {
        confirm.setCustomValidity('');
        confirm.removeAttribute('aria-invalid');
        return false;
      }

      const ok = p === c;
      confirm.toggleAttribute('aria-invalid', !ok);
      confirm.setCustomValidity(ok ? '' : 'Passwords do not match');

      if (!ok && typeof confirm.reportValidity === 'function') {
        confirm.reportValidity();
      }
      return ok;
    };

    const validateDOB = () => {
      if (!dob) return true;
      const raw = (dob.value || '').trim();
      if (!raw) {
        dob.setCustomValidity('');
        return false;
      }

      const selected = new Date(raw);
      if (Number.isNaN(selected.getTime())) {
        dob.setCustomValidity('Enter a valid date');
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selected.setHours(0, 0, 0, 0);

      if (selected >= today) {
        dob.setCustomValidity('Date of birth must be earlier than today');
        return false;
      }

      let age = today.getFullYear() - selected.getFullYear();
      const monthDiff = today.getMonth() - selected.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < selected.getDate())) age -= 1;

      if (age < 18) {
        dob.setCustomValidity('You must be at least 18 years old');
        return false;
      }

      dob.setCustomValidity('');
      return true;
    };

    const validatePrivacy = () => {
      if (!privacy) return true;
      const ok = privacy.checked;
      privacy.setCustomValidity(ok ? '' : 'Please accept to continue');
      return ok;
    };

    const updateSubmitState = () => {
      sanitizeNames();
      const allFilled = requiredEls.every(isRequiredFilled);
      const passOk = validatePasswordMatch();
      const dobOk = validateDOB();
      const privacyOk = validatePrivacy();
      setSubmitEnabled(allFilled && passOk && dobOk && privacyOk);
    };

    if (passwordErrorEl) { passwordErrorEl.hidden = true; passwordErrorEl.style.display = 'none'; }

    setSubmitEnabled(false);
    updateSubmitState();
    syncDobFilledState();

    Array.from(form.querySelectorAll('input, select, textarea')).forEach((el) => {
      el.addEventListener('input', updateSubmitState);
      el.addEventListener('change', updateSubmitState);
    });

    dob?.addEventListener('input', syncDobFilledState);
    dob?.addEventListener('change', syncDobFilledState);

    dob?.addEventListener('change', () => {
      if (!validateDOB()) {
        dob?.reportValidity?.();
      }
    });

    firstName?.addEventListener('input', sanitizeNames);
    lastName?.addEventListener('input', sanitizeNames);

    password?.addEventListener('input', () => {
      if (passwordErrorEl && !passwordErrorEl.hidden && isPasswordStrong(password.value || '')) {
        showPasswordError(false);
      }
    });

    submitBtn.addEventListener('click', (event) => {
      if (blockWeakPassword(event)) return;
    });

    form.addEventListener('submit', (event) => {
      sanitizeNames();
      updateSubmitState();

      if (blockWeakPassword(event)) return;

      if (!validateDOB()) {
        event.preventDefault();
        dob?.reportValidity?.();
        dob?.focus?.({ preventScroll: true });
        return;
      }

      if (submitBtn.disabled) {
        event.preventDefault();
      }
    });
  });
}

function initSelectIcons(root = document) {
  root.querySelectorAll('.field-select-icon').forEach((icon) => {
    if (icon.dataset.selectIconInit === 'true') return;
    icon.dataset.selectIconInit = 'true';

    const field = icon.closest('.field--select');
    const select = field?.querySelector('select');
    if (!select) return;

    const blockSelectOpen = (event) => {
      if (event.target !== select) return;
      if (event.button !== 0) return;
      event.preventDefault();
      select.focus({ preventScroll: true });
    };

    select.addEventListener('mousedown', blockSelectOpen);

    icon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      select.focus({ preventScroll: true });
      if (typeof select.showPicker === 'function') {
        select.showPicker();
      }
    });
  });
}

function initAuthUI(root = document) {
  initPasswordToggles(root);
  initConfirmPasswordValidation(root);
  initRegisterNoteComposer(root);
  initRegisterNameMapper(root);
  initRegisterFormValidation(root);
  initSelectIcons(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAuthUI());
} else {
  initAuthUI();
}

document.addEventListener('shopify:section:load', (event) => {
  initAuthUI(event.target);
});
